import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchCampaigns, fetchVendors, fetchCampaignTypes,
    createCampaign, updateCampaign, deleteCampaign,
} from '../../api';
import { useAccess } from '../auth/AccessContext';
import DataGrid from '../common/DataGrid';
import Modal from './Modal';
import { actionsColumn, formatDate, moneyColumn, toISODate } from './shared';
import '../reports/Reports.css';
import './Marketing.css';

// Sentinel value for the vendor dropdown's "new vendor" choice, which swaps the
// select for a text input so a vendor can be created without leaving the form.
const NEW_VENDOR = '__new__';

const EMPTY_FORM = { vendorId: '', vendorName: '', name: '', source: '', type: '', costPerLead: '', notes: '' };

// Campaign management. A campaign belongs to one vendor and carries a name, a
// source (the attribution name it reports under), and a channel type. Spend is
// recorded against campaigns, so a campaign with spend days can't be deleted.
export default function Campaigns() {
    // A vendor-limited profile only sees (and can only pick) its own vendors,
    // and can't create one inline — the server refuses that too.
    const { vendorScope } = useAccess();
    const [campaigns, setCampaigns] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [campaignData, vendorData] = await Promise.all([fetchCampaigns(), fetchVendors()]);
            setCampaigns(campaignData || []);
            setVendors(vendorData || []);
        } catch (err) {
            setError(err.message);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // The channel list is server-owned so it stays in step with the validation
    // that guards it; fall back to an empty list if it can't be fetched.
    useEffect(() => {
        fetchCampaignTypes().then(setTypes).catch(() => setTypes([]));
    }, []);

    const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = useCallback((campaign) => {
        setForm({
            vendorId: String(campaign.vendor_id),
            vendorName: '',
            name: campaign.name || '',
            source: campaign.source || '',
            type: campaign.type || '',
            costPerLead: campaign.cost_per_lead == null ? '' : String(campaign.cost_per_lead),
            notes: campaign.notes || '',
        });
        setDialogError(null);
        setDialog({ mode: 'edit', campaign });
    }, []);

    const openDelete = useCallback((campaign) => {
        setDialogError(null);
        setDialog({ mode: 'delete', campaign });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    const creatingVendor = form.vendorId === NEW_VENDOR;

    // Ready to submit once there's a campaign name and either a chosen vendor or
    // a name for a new one.
    const formValid =
        form.name.trim() !== '' &&
        (creatingVendor ? form.vendorName.trim() !== '' : form.vendorId !== '');

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialog.mode === 'delete') {
                await deleteCampaign(dialog.campaign.id);
            } else {
                // vendor_id 0 tells the server to find-or-create by vendor_name.
                // Blank optional fields go as null so they store as NULL.
                const payload = {
                    vendor_id: creatingVendor ? 0 : Number(form.vendorId),
                    vendor_name: creatingVendor ? form.vendorName.trim() : '',
                    name: form.name.trim(),
                    source: form.source.trim() || null,
                    type: form.type || null,
                    cost_per_lead: form.costPerLead.trim() === '' ? null : Number(form.costPerLead),
                    notes: form.notes.trim() || null,
                };
                if (dialog.mode === 'create') await createCampaign(payload);
                else await updateCampaign(dialog.campaign.id, payload);
            }
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const columnDefs = useMemo(() => [
        { field: 'name', headerName: 'Campaign', flex: 1, minWidth: 200 },
        { field: 'vendor_name', headerName: 'Vendor', width: 180 },
        { field: 'type', headerName: 'Type', width: 140 },
        { field: 'source', headerName: 'Source', width: 180 },
        // A rate, so grouping should average it rather than sum it like the
        // spend columns elsewhere.
        { ...moneyColumn('cost_per_lead', 'Cost Per Lead'), aggFunc: 'avg' },
        { field: 'spends', headerName: 'Spend Days', type: 'rightAligned', width: 140, filter: 'agNumberColumnFilter' },
        { field: 'notes', headerName: 'Notes', width: 240, tooltipField: 'notes' },
        { field: 'created_at', headerName: 'Created', width: 140, valueFormatter: (p) => formatDate(p.value) },
        actionsColumn(openEdit, openDelete),
    ], [openEdit, openDelete]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 120 }), []);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Campaigns</h1>
                    <p className="page__subtitle">
                        Campaigns belong to a vendor and are what spend is recorded against.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={openCreate}>+ Add Campaign</button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}

            {loading && <p className="page__subtitle">Loading campaigns…</p>}

            {!loading && !error && campaigns.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    No campaigns yet. Add one to start recording spend.
                </p>
            )}

            {campaigns.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <DataGrid
                        rowData={campaigns}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        height="calc(100vh - 260px)"
                        enableExport
                        exportFileName={() => `Campaigns_${toISODate(new Date())}`}
                    />
                </div>
            )}

            {dialog && dialog.mode !== 'delete' && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add Campaign' : 'Edit Campaign'}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add Campaign' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!formValid}
                    error={dialogError}
                >
                    <label className="mkt__field">
                        <span>Vendor</span>
                        <select value={form.vendorId} onChange={setField('vendorId')}>
                            <option value="">Select a vendor…</option>
                            {vendors.map((v) => (
                                <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                            {!vendorScope.scoped && <option value={NEW_VENDOR}>+ New vendor…</option>}
                        </select>
                    </label>

                    {creatingVendor && (
                        <label className="mkt__field">
                            <span>New Vendor Name</span>
                            <input
                                type="text"
                                value={form.vendorName}
                                onChange={setField('vendorName')}
                                placeholder="e.g. Google Ads"
                                autoFocus
                            />
                            <span style={{ fontSize: '0.78rem', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', color: '#7a8fa0' }}>
                                Created with this campaign. An existing vendor with this name is reused.
                            </span>
                        </label>
                    )}

                    <label className="mkt__field">
                        <span>Campaign Name</span>
                        <input
                            type="text"
                            value={form.name}
                            onChange={setField('name')}
                            placeholder="e.g. Search - Brand"
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Source</span>
                        <input
                            type="text"
                            value={form.source}
                            onChange={setField('source')}
                            placeholder="e.g. google-brand"
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Type</span>
                        <select value={form.type} onChange={setField('type')}>
                            <option value="">Select a type…</option>
                            {types.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </label>

                    <label className="mkt__field">
                        <span>Cost Per Lead</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.costPerLead}
                            onChange={setField('costPerLead')}
                            placeholder="0.00"
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Notes</span>
                        <textarea
                            className="mkt__textarea"
                            rows={3}
                            value={form.notes}
                            onChange={setField('notes')}
                            placeholder="Anything worth remembering about this campaign"
                        />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Campaign"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Campaign"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete <strong>{dialog.campaign.name}</strong> ({dialog.campaign.vendor_name})? This can't be undone.
                    </p>
                    {dialog.campaign.spends > 0 && (
                        <p className="mkt-modal__text">
                            It has {dialog.campaign.spends} spend day{dialog.campaign.spends === 1 ? '' : 's'} recorded,
                            so it can't be deleted until {dialog.campaign.spends === 1 ? 'it is' : 'they are'} removed
                            from the Spends page.
                        </p>
                    )}
                </Modal>
            )}
        </div>
    );
}
